import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const Services = () => {
  const [activeFilter, setActiveFilter] = useState('Todos');

  const filters = ['Todos', 'Valet Parking', 'Logística'];

  const services = [
    {
      category: 'Valet Parking',
      title: 'Valet Parking para Eventos',
      description:
        'Gestão completa de estacionamento para eventos corporativos e sociais, com equipe treinada, processos padronizados e atendimento premium.',
      icon: 'ri-parking-box-line',
      benefits: [
        'Equipe uniformizada',
        'Gestão de fluxo',
        'Seguro dos veículos durante toda a operação',
        'Atendimento personalizado',
      ],
    },
    {
      category: 'Valet Parking',
      title: 'Motorista Executivo',
      description:
        'Serviço de motorista executivo para transporte corporativo, reuniões, eventos e deslocamentos empresariais com total discrição.',
      icon: 'ri-steering-2-line',
      benefits: [
        'Motoristas profissionais',
        'Veículos executivos',
        'Pontualidade garantida',
        'Discrição total',
      ],
    },
    {
      category: 'Logística',
      title: 'Logística para Eventos',
      description:
        'Planejamento e execução logística completa para eventos corporativos, incluindo transporte de estruturas e organização de materiais.',
      icon: 'ri-box-3-line',
      benefits: [
        'Planejamento logístico',
        'Coordenação de equipes',
        'Montagem e desmontagem',
        'Gestão de prazos',
      ],
    },
    {
      category: 'Logística',
      title: 'Movimentação de Mobiliário',
      description:
        'Movimentação especializada de equipamentos, máquinas e estruturas corporativas com técnicas adequadas e profissionais capacitados.',
      icon: 'ri-truck-line',
      benefits: [
        'Equipamentos especializados',
        'Técnicas de segurança',
        'Profissionais capacitados',
        'Planejamento técnico',
      ],
    },
  ];

  const filteredServices =
    activeFilter === 'Todos'
      ? services
      : services.filter((service) => service.category === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="relative bg-primary pt-32 pb-20 overflow-hidden">
        <div className="relative w-full px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.2em] text-white/40 uppercase mb-4">
              Serviços
            </span>
            <h1 className="text-4xl lg:text-5xl font-medium text-white leading-tight mb-8">
              Nossos Serviços
            </h1>

            {/* Filters */}
            <div className="flex items-center justify-center gap-3">
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeFilter === filter
                      ? 'bg-white text-primary'
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28">
        <div className="w-full px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-light rounded-2xl p-10 hover:shadow-xl transition-all duration-500 group"
              >
                <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center mb-8 group-hover:bg-primary group-hover:scale-110 transition-all duration-500">
                  <i className={`${service.icon} text-3xl text-primary group-hover:text-white transition-colors duration-500`}></i>
                </div>

                <span className="text-xs font-medium tracking-[0.15em] text-secondary/50 uppercase">
                  {service.category}
                </span>

                <h3 className="text-2xl font-semibold text-primary mt-2 mb-4">
                  {service.title}
                </h3>

                <p className="text-secondary text-base leading-relaxed mb-8">
                  {service.description}
                </p>

                <div className="grid grid-cols-2 gap-3 mb-8">
                  {service.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <i className="ri-check-line text-sm text-primary"></i>
                      <span className="text-secondary text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contato"
                  className="inline-flex items-center gap-2 text-primary font-medium text-sm group-hover:gap-4 transition-all duration-300 cursor-pointer"
                >
                  Solicitar Orçamento
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-accent py-24 lg:py-32">
        <div className="w-full px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-2xl lg:text-3xl font-medium text-primary mb-6">
              Precisa de uma Solução Personalizada?
            </h2>
            <p className="text-secondary text-lg mb-10">
              Entre em contato e receba um orçamento customizado para as necessidades da sua empresa ou evento.
            </p>
            <Link
              to="/contato"
              className="inline-flex items-center gap-3 bg-primary text-white font-semibold text-sm px-10 py-5 rounded-full hover:bg-primary/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Falar com Especialista
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
      
      {/* Botão Flutuante WhatsApp */}
      <a
        href="https://wa.me/5541997438126"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 bg-[#25D366] text-white w-14 h-14 rounded-full flex items-center justify-center shadow-xl hover:bg-[#20BA5A] hover:scale-110 transition-all duration-300 z-50 cursor-pointer"
        aria-label="WhatsApp"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </div>
  );
};

export default Services;
